import { useEffect, useRef, useState } from 'react'

import { Button, Dialog } from '@clientbase/clientbase-library'

import { Coupon } from '../../types/coupon'

export type CouponAction =
  | 'pause'
  | 'unpause'
  | 'finish'
  | 'reactivate'
  | 'delete'

interface CouponActionDialogProps {
  open: boolean
  action: CouponAction | null
  coupon: Coupon | null
  loading?: boolean
  onClose: () => void
  onConfirm: () => void
}

const ACTION_CONTENT: Record<
  CouponAction,
  {
    title: string
    description: (code: string) => string
    confirmLabel: string
    icon: string
  }
> = {
  pause: {
    title: 'Pausar cupom',
    description: (code) =>
      `Confirma pausar o cupom ${code}? Ele não poderá ser usado até ser despausado.`,
    confirmLabel: 'Pausar',
    icon: 'faTicket'
  },
  unpause: {
    title: 'Despausar cupom',
    description: (code) =>
      `Confirma despausar o cupom ${code}? Ele voltará a ficar disponível para uso.`,
    confirmLabel: 'Despausar',
    icon: 'faTicket'
  },
  finish: {
    title: 'Finalizar cupom',
    description: (code) =>
      `Confirma finalizar o cupom ${code}? Após finalizado, ele pode ser reativado.`,
    confirmLabel: 'Finalizar',
    icon: 'faTicket'
  },
  reactivate: {
    title: 'Reativar cupom',
    description: (code) =>
      `Confirma reativar o cupom ${code}? Ele voltará a ficar disponível para uso.`,
    confirmLabel: 'Reativar',
    icon: 'faTicket'
  },
  delete: {
    title: 'Excluir cupom',
    description: (code) =>
      `Confirma excluir o cupom ${code}? Esta ação não pode ser desfeita.`,
    confirmLabel: 'Excluir',
    icon: 'delete'
  }
}

const CouponActionDialog = ({
  open,
  action,
  coupon,
  loading,
  onClose,
  onConfirm
}: CouponActionDialogProps) => {
  const [displayed, setDisplayed] = useState<{
    action: CouponAction
    code: string
  } | null>(null)
  const lastCodeRef = useRef('')

  useEffect(() => {
    if (action) {
      const code = coupon?.code ?? lastCodeRef.current
      lastCodeRef.current = code
      setDisplayed({ action, code })
    }
  }, [action, coupon?.code])

  const content = displayed ? ACTION_CONTENT[displayed.action] : null
  const code = displayed?.code ?? ''

  return (
    <Dialog
      icon={(content?.icon ?? 'faTicket') as any}
      title={content?.title ?? ''}
      description={content ? content.description(code) : ''}
      isOpenDialog={open}
      setIsOpenDialog={onClose}
      cancelButton
    >
      <Button
        variantButton="action"
        onClick={onConfirm}
        type="button"
        disabled={loading}
        loading={loading}
        fullWidth
      >
        {content?.confirmLabel ?? 'Confirmar'}
      </Button>
    </Dialog>
  )
}

export default CouponActionDialog
